import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { useEnabledModuleTabPaths } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/useEnabledModuleTabPaths';
import { usePrepModuleTabProps } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/usePrepModuleTabProps';
import {
  EmployeeModuleRouting_Rewarding,
  EmployeeModuleTabPath_Rewarding,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/RewardingModule/data';
import {
  EmployeeModuleTabAccessMode,
  EmployeeModuleTabProps,
  EmployeeModuleTabSetupProps,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/types';

type PropsType = {
  hasFullAccess: boolean;
  modulePath: string;
};

type UseEmployeeRewardingModuleOutput<TabPath> = {
  isLoading: boolean;
  employeeId: number;
  enabledTabPaths: TabPath[];
  defaultTab: TabPath;
  modulePath: string;
  tabs: EmployeeModuleTabProps<TabPath>[];
};

export const useEmployeeRewardingModule = (
  props: PropsType,
): UseEmployeeRewardingModuleOutput<EmployeeModuleTabPath_Rewarding> => {
  const { hasFullAccess, modulePath } = props;

  const { t } = useTranslation();
  const { selectedEmployeeId } = useSelectedEmployeeId();

  const { availableTabs } = useMemo(() => {
    const allTabs = Object.values(EmployeeModuleTabPath_Rewarding);

    // NOTE: No conditions yet, but likely added later
    const tabConditions: {
      [key in EmployeeModuleTabPath_Rewarding]?: boolean;
    } = {
      // [EmployeeModuleTabPath_People.DecisionRegistry]:
      // hasData &&
      // applicationConfig?.fComConfig?.miscellaneousConfig
      //   ?.fComEnableDecisionRegistry,
    };

    const availableTabs = hasFullAccess
      ? allTabs.filter((path) => {
          return Object.keys(tabConditions).includes(path)
            ? tabConditions[path]
            : true;
        })
      : [];

    return { tabConditions, availableTabs };
  }, [hasFullAccess]);

  const tabProps: EmployeeModuleTabSetupProps<EmployeeModuleTabPath_Rewarding>[] =
    [
      {
        id: EmployeeModuleTabPath_Rewarding.General,
        title: t('laGeneral'),
      },
      {
        id: EmployeeModuleTabPath_Rewarding.Incentives,
        title: t('laIncentives'),
      },
      {
        id: EmployeeModuleTabPath_Rewarding.BillableHours,
        title: t('laBillableHours'),
      },
      {
        id: EmployeeModuleTabPath_Rewarding.Benefits,
        title: t('laBenefits'),
      },
      {
        id: EmployeeModuleTabPath_Rewarding.Acknowledgements,
        title: t('laAcknowledgements'),
      },
      {
        id: EmployeeModuleTabPath_Rewarding.BankAccount,
        title: t('laBankAccount'),
      },
      {
        id: EmployeeModuleTabPath_Rewarding.Documents,
        title: t('laEmpDocs'),
      },
      {
        id: EmployeeModuleTabPath_Rewarding.CustomData,
        title: t('laCustomData'),
      },
      {
        id: EmployeeModuleTabPath_Rewarding.Summary,
        title: t('laSummary'),
      },
      {
        id: EmployeeModuleTabPath_Rewarding.WorkTime,
        title: t('laWorktime'),
      },
    ].map((p) => ({ ...p, accessMode: EmployeeModuleTabAccessMode.Full }));

  const { enabledTabConfigs, enabledTabPaths, defaultTab } =
    useEnabledModuleTabPaths({
      ...EmployeeModuleRouting_Rewarding,
      availableTabs,
    });

  const { tabs, isLoading: isLoadingTabPrep } = usePrepModuleTabProps({
    ...EmployeeModuleRouting_Rewarding,
    tabConfigs: enabledTabConfigs,
    modulePath,
    tabsSetupProps: tabProps,
    enabledTabPaths,
  });

  return {
    isLoading: isLoadingTabPrep,
    enabledTabPaths,
    defaultTab,
    modulePath,
    employeeId: selectedEmployeeId,
    tabs,
  };
};
